/**
 * Timezone-aware calendar helpers.
 *
 * Every "day" in this application (streaks, heatmap, daily planner, weekly and
 * monthly reports) is a *local* calendar day resolved in `UserProfile.timezone`.
 * A `LocalDay` is always the string `YYYY-MM-DD`; instants are always UTC Dates.
 *
 * Implemented with `Intl` only — no date library, so the exact same code runs in
 * Node, the browser and Hermes.
 */

export type LocalDay = string;

const DAY_MS = 86_400_000;

const partsFormatter = (timeZone: string) =>
  new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

const formatterCache = new Map<string, Intl.DateTimeFormat>();

function getFormatter(timeZone: string): Intl.DateTimeFormat {
  let cached = formatterCache.get(timeZone);
  if (!cached) {
    cached = partsFormatter(timeZone);
    formatterCache.set(timeZone, cached);
  }
  return cached;
}

interface WallClock {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

function wallClockIn(instant: Date, timeZone: string): WallClock {
  const parts = getFormatter(timeZone).formatToParts(instant);
  const get = (type: Intl.DateTimeFormatPartTypes): number => {
    const part = parts.find((p) => p.type === type);
    return part ? Number(part.value) : 0;
  };
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour') % 24,
    minute: get('minute'),
    second: get('second'),
  };
}

/** Offset of `timeZone` at `instant`, in milliseconds (east of UTC is positive). */
export function timezoneOffsetMs(instant: Date, timeZone: string): number {
  const wc = wallClockIn(instant, timeZone);
  const asUtc = Date.UTC(wc.year, wc.month - 1, wc.day, wc.hour, wc.minute, wc.second);
  // `asUtc` has second precision, so compare against a second-truncated instant.
  return asUtc - Math.floor(instant.getTime() / 1000) * 1000;
}

const pad = (n: number, width = 2): string => String(n).padStart(width, '0');

/** The local calendar day that `instant` falls on in `timeZone`. */
export function toLocalDay(instant: Date | string, timeZone: string): LocalDay {
  const date = typeof instant === 'string' ? new Date(instant) : instant;
  const wc = wallClockIn(date, timeZone);
  return `${pad(wc.year, 4)}-${pad(wc.month)}-${pad(wc.day)}`;
}

/** UTC instant of local midnight starting `day` in `timeZone` (DST-safe). */
export function startOfLocalDay(day: LocalDay, timeZone: string): Date {
  const naive = Date.parse(`${day}T00:00:00.000Z`);
  if (Number.isNaN(naive)) throw new RangeError(`invalid local day: ${day}`);
  let guess = naive - timezoneOffsetMs(new Date(naive), timeZone);
  // Re-check once: the offset may differ on the far side of a DST transition.
  const refined = naive - timezoneOffsetMs(new Date(guess), timeZone);
  if (refined !== guess) guess = refined;
  return new Date(guess);
}

/** UTC instant that ends `day` (i.e. the start of the next local day). */
export function endOfLocalDay(day: LocalDay, timeZone: string): Date {
  return startOfLocalDay(addDays(day, 1), timeZone);
}

/** Today's local day in `timeZone`. */
export function todayLocalDay(timeZone: string, now: Date = new Date()): LocalDay {
  return toLocalDay(now, timeZone);
}

/** Pure calendar arithmetic on `YYYY-MM-DD` — never touches timezones. */
export function addDays(day: LocalDay, amount: number): LocalDay {
  const ts = Date.parse(`${day}T00:00:00.000Z`);
  if (Number.isNaN(ts)) throw new RangeError(`invalid local day: ${day}`);
  const next = new Date(ts + amount * DAY_MS);
  return `${pad(next.getUTCFullYear(), 4)}-${pad(next.getUTCMonth() + 1)}-${pad(next.getUTCDate())}`;
}

export function daysBetween(from: LocalDay, to: LocalDay): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS);
}

export function compareDays(a: LocalDay, b: LocalDay): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** Inclusive list of every day from `from` to `to`. */
export function enumerateDays(from: LocalDay, to: LocalDay): LocalDay[] {
  const total = daysBetween(from, to);
  if (total < 0) return [];
  const days: LocalDay[] = [];
  for (let i = 0; i <= total; i += 1) days.push(addDays(from, i));
  return days;
}

/** 1 = Monday … 7 = Sunday. */
export function isoWeekday(day: LocalDay): number {
  const dow = new Date(`${day}T00:00:00Z`).getUTCDay();
  return dow === 0 ? 7 : dow;
}

/** Monday of the ISO week containing `day`. */
export function startOfIsoWeek(day: LocalDay): LocalDay {
  return addDays(day, -(isoWeekday(day) - 1));
}

export function endOfIsoWeek(day: LocalDay): LocalDay {
  return addDays(startOfIsoWeek(day), 6);
}

/** ISO-8601 week key, e.g. `2026-W31`. */
export function isoWeekKey(day: LocalDay): string {
  const thursday = addDays(startOfIsoWeek(day), 3);
  const year = Number(thursday.slice(0, 4));
  const jan4 = `${pad(year, 4)}-01-04`;
  const week = Math.floor(daysBetween(startOfIsoWeek(jan4), thursday) / 7) + 1;
  return `${pad(year, 4)}-W${pad(week)}`;
}

/** Monday of the ISO week identified by `2026-W31`. */
export function isoWeekStart(weekKey: string): LocalDay {
  const match = /^(\d{4})-W(\d{2})$/.exec(weekKey);
  if (!match) throw new RangeError(`invalid ISO week key: ${weekKey}`);
  const year = Number(match[1]);
  const week = Number(match[2]);
  const firstWeekMonday = startOfIsoWeek(`${pad(year, 4)}-01-04`);
  return addDays(firstWeekMonday, (week - 1) * 7);
}

/** `2026-07` for the month containing `day`. */
export function monthKey(day: LocalDay): string {
  return day.slice(0, 7);
}

export function startOfMonth(monthOrDay: string): LocalDay {
  return `${monthOrDay.slice(0, 7)}-01`;
}

export function endOfMonth(monthOrDay: string): LocalDay {
  const month = monthOrDay.slice(0, 7);
  const year = Number(month.slice(0, 4));
  const monthIndex = Number(month.slice(5, 7));
  // Day 0 of the *next* month is the last day of this one.
  const last = new Date(Date.UTC(year, monthIndex, 0));
  return `${pad(last.getUTCFullYear(), 4)}-${pad(last.getUTCMonth() + 1)}-${pad(last.getUTCDate())}`;
}

export interface StreakSummary {
  current: number;
  longest: number;
  /** Lengths of every consecutive run of active days, oldest first. */
  runs: number[];
}

/**
 * Streak maths over a set of active local days.
 *
 * The current streak is the run ending today; if today has no activity yet the
 * run ending yesterday still counts (the day is not over).
 */
export function computeStreaks(activeDays: Iterable<LocalDay>, today: LocalDay): StreakSummary {
  const sorted = Array.from(new Set(activeDays)).sort();
  if (sorted.length === 0) return { current: 0, longest: 0, runs: [] };

  const runs: number[] = [];
  let runLength = 0;
  let previous: LocalDay | null = null;

  for (const day of sorted) {
    if (previous !== null && daysBetween(previous, day) === 1) {
      runLength += 1;
    } else {
      if (previous !== null) runs.push(runLength);
      runLength = 1;
    }
    previous = day;
  }
  if (previous !== null) runs.push(runLength);
  const runEnd = previous;

  const longest = runs.reduce((max, n) => Math.max(max, n), 0);

  let current = 0;
  if (runEnd !== null) {
    const gap = daysBetween(runEnd, today);
    if (gap === 0 || gap === 1) current = runs[runs.length - 1] ?? 0;
  }

  return { current, longest, runs };
}

/** Best-effort IANA timezone of the host machine. */
export function detectTimezone(fallback = 'UTC'): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || fallback;
  } catch {
    return fallback;
  }
}

export function formatDayLabel(day: LocalDay): string {
  const [, month, dayOfMonth] = day.split('-');
  return `${month}/${dayOfMonth}`;
}
